import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DeviceDetail } from './DeviceDetail';
import { DeviceImportScreen } from './DeviceImportScreen';
import { ReaderList } from './ReaderList';
import { SensorList } from './SensorList';
import { SegmentedControl } from '../../design-system/primitives/SegmentedControl/SegmentedControl';
import { PageActions } from '../../app/shell/PageActions';
import styles from './DeviceScreen.module.css';

/**
 * The devices screen — sensors and readers as two views (`SensorList`,
 * `ReaderList`), and the device detail beneath either one (`DeviceDetail`).
 *
 * Follows `ReferenceScreen.tsx` for the segmented-control shape: one list
 * mounted at a time, not all views kept alive together, so switching tabs
 * does not fire every kind's request the instant this screen opens.
 *
 * READING NEEDS NO PERMISSION — every `GET /devices/*` route needs only
 * `auth:sanctum`/`active` (`devices.ts`'s own docblock echoes `reference.ts`'s
 * identical note), so both lists render for whoever reaches this screen.
 * `permissions` travels down only so each list can decide what to render
 * BESIDES the table: `device.manage` gates every create/edit/status control,
 * the same shape `ReferenceScreen.tsx` established for `reference.manage`.
 *
 * ## Selection is local state, not a router
 *
 * This application has no router (`AuditLogScreen.tsx`'s own docblock,
 * `App.tsx`'s own docblock). `/devices/:kind/:uuid` (this task's own brief)
 * is therefore not a URL this screen parses — it is the shape of the two
 * pieces of state below (`kind`+`uuid`), held exactly the way `App.tsx`
 * itself holds `activePath` as `useState`, not as history.
 *
 * ## The active list is NEVER unmounted to show a detail (review finding 1)
 *
 * An earlier version of this component returned `<DeviceDetail ... />`
 * early, which UNMOUNTS `SensorList`/`ReaderList` outright the moment a row
 * opens — every piece of state that component owns (`draftFilters`,
 * `appliedFilters`, `cursor`) is destroyed with it, so `onBack` came back to
 * a freshly-mounted list on an unfiltered page one, having silently thrown
 * away whatever search an agent was in the middle of. The component's own
 * comment claimed the opposite at the time, which is worse than no comment
 * at all — a reader trusting it would never think to check.
 *
 * The list for the active `kind` is ALWAYS mounted below, in a `hidden` div
 * — never behind a ternary that unmounts it. Opening a row only adds
 * `<DeviceDetail>` as a sibling and hides the list with the native `hidden`
 * attribute (out of the accessibility tree and the visual layout, but very
 * much still alive in React's tree). `onBack` clears `selected`, the
 * `hidden` attribute comes off, and the list an agent was looking at
 * reappears exactly as they left it — no refetch, because there was never
 * an unmount to recover from. `DeviceScreenTest`'s own
 * "restores the filter and page after returning from a detail" test is the
 * proof; reverting this to the early-return shape turns it red.
 *
 * Switching the `kind` tab (`SegmentedControl`'s own `onChange`) is
 * unaffected: only the tab and title are hidden alongside the list while a
 * detail is open, so a caller cannot switch tabs mid-detail — they see
 * `onBack` first, exactly as before.
 *
 * ## The Import tab (task 11) — the identical shape `ReferenceScreen.tsx`
 * already established for centres
 *
 * `import` is a THIRD tab value, not a fourth permanently-mounted screen:
 * gated entirely on `device.manage` (the same ability `POST
 * /devices/{kind}/import` itself needs — `routes/api.php`'s own
 * `can:manage,...Class` on both import routes), and hidden from the tab
 * strip rather than rendered inert, mirroring `App.tsx`'s own `auditLog` nav
 * entry and `ReferenceScreen.tsx`'s own Import tab. A caller who loses
 * `device.manage` between renders falls back to the sensor tab rather than
 * staying parked on one that no longer exists — `activeTab` below is
 * `ReferenceScreen.tsx`'s own `activeTab` fallback, verbatim.
 *
 * `DeviceDetail` is unreachable from the import tab (`DeviceImportScreen`
 * never calls `onOpen`), so `selected` is always `null` while it is active —
 * no interaction between this branch and the "list stays mounted" rule two
 * paragraphs up.
 */

export interface DeviceScreenProps {
  permissions: readonly string[];
}

type Kind = 'sensor' | 'reader';
type Tab = Kind | 'import';

interface Selected {
  kind: Kind;
  uuid: string;
}

export function DeviceScreen({ permissions }: DeviceScreenProps) {
  const { t } = useTranslation();
  const canManage = permissions.includes('device.manage');

  const [tab, setTab] = useState<Tab>('sensor');
  const [selected, setSelected] = useState<Selected | null>(null);

  const options = [
    { value: 'sensor', label: t('devices.tabSensors') },
    { value: 'reader', label: t('devices.tabReaders') },
    ...(canManage ? [{ value: 'import', label: t('devices.tabImport') }] : []),
  ];

  // See this file's own docblock: a caller who lost `device.manage` between
  // renders must not stay parked on a tab that no longer exists.
  const activeTab: Tab = tab === 'import' && !canManage ? 'sensor' : tab;

  return (
    <div className={styles['page']}>
      {selected === null ? (
        // The tab switch rides in the shell's title row rather than spending
        // a row of its own on the page ground.
        <PageActions>
          <SegmentedControl
            label={t('devices.tabsLabel')}
            labelPlacement="inline"
            options={options}
            value={activeTab}
            onChange={(value) => {
              setTab(value as Tab);
            }}
          />
        </PageActions>
      ) : (
        <DeviceDetail
          kind={selected.kind}
          uuid={selected.uuid}
          permissions={permissions}
          onBack={() => {
            setSelected(null);
          }}
        />
      )}

      {activeTab === 'import' ? (
        <DeviceImportScreen permissions={permissions} />
      ) : (
        /*
          `hidden`, never a ternary that unmounts — see this file's own
          docblock. The list stays alive underneath a detail view exactly the
          way it was before opening one, so `onBack` restores it rather than
          re-fetching it from scratch.
        */
        <div hidden={selected !== null}>
          {activeTab === 'sensor' ? (
            <SensorList
              permissions={permissions}
              active={selected === null}
              onOpen={(uuid) => {
                setSelected({ kind: 'sensor', uuid });
              }}
            />
          ) : (
            <ReaderList
              permissions={permissions}
              active={selected === null}
              onOpen={(uuid) => {
                setSelected({ kind: 'reader', uuid });
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
